/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useEffect, useRef } from "react"
import { Form } from "grab-ui"
import { Checkbox, Input, Row } from "@duxton-web/ui"
import _intersection from "lodash/intersection"
import { InventoryCategory } from "types/delv-platform-api/inventoryCategory"
import { BULK_UPLOAD_EXPORT_FILE_FIELDS } from "constants/menu_bulk_upload"
import { useTranslations } from "common/utils/i18n"
import { useInventoryCategoryCheckAllBehavior } from "../hooks/useInventoryCategoryCheckAllBehavior"
import {
  checkAllOption,
  checkAllRowWrap,
  checkboxGroupCss,
  checkboxGroupWrapCss,
  formInputCss,
  selectionWrapCss,
  textCommonCss,
} from "./styles"

interface PropTypes extends FormComponentProps {
  inventoryCategories: InventoryCategory[]
}

const InventoryCategoryCheckbox = ({
  inventoryCategories,
  form,
}: PropTypes) => {
  const [allCategoriesText, searchByCategoryPlaceholderText] = useTranslations([
    "bulkUpload.drawers.downloadInventory.allCategory",
    "bulkUpload.drawers.downloadInventory.searchByCategoryPlaceholder",
  ])
  const { getFieldDecorator } = form
  const selectableCategoryIDs = inventoryCategories.map(cat => cat.categoryID)
  const selectedCategoryIDs = form.getFieldValue(
    BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryCheckboxGroup,
  )
  const isSelectedAllCategories =
    !!selectedCategoryIDs?.length &&
    selectedCategoryIDs.length === selectableCategoryIDs.length

  const doCheckAll = () => {
    form.setFieldsValue({
      [BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryCheckboxGroup]: selectableCategoryIDs,
    })
  }

  const doUncheckAll = () => {
    form.setFieldsValue({
      [BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryCheckboxGroup]: [],
    })
  }

  const {
    onCheckAllChange,
    checkAllChecked,
    checkAllIndeterminate,
  } = useInventoryCategoryCheckAllBehavior({
    doCheckAll,
    doUncheckAll,
    isSelectedAllCategories,
    selectedCategoryIDs,
  })

  // SHOULD NOT USE THIS HACK SOLUTION TO SIMULATE ComponentDidMount
  // Context Form in Antd 3.0 does not support to subscribe values changed in component (onValueChanges need to be passed in Form.create which is out of component)
  // -> need to use this hack solution or use initialValue in Form.Item and listen to every update in its parent component (useEffect with no dependency array)
  // I prefer to use this solution
  const doneFirstDoCheckAll = useRef(false)
  useEffect(() => {
    if (selectableCategoryIDs.length > 0 && !doneFirstDoCheckAll.current) {
      doneFirstDoCheckAll.current = true
      doCheckAll()
    }
  }, [selectableCategoryIDs])

  // side effect when filter category list by keyword
  useEffect(() => {
    const validSelectedCategoryIDs = _intersection(
      selectedCategoryIDs,
      selectableCategoryIDs,
    )
    if (
      validSelectedCategoryIDs &&
      selectedCategoryIDs &&
      validSelectedCategoryIDs.length !== selectedCategoryIDs.length
    ) {
      form.setFieldsValue({
        [BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryCheckboxGroup]: validSelectedCategoryIDs,
      })
    }
  }, [form, selectedCategoryIDs, selectableCategoryIDs])

  return (
    <div css={selectionWrapCss}>
      <Row align="middle" justify="space-between" css={checkAllRowWrap}>
        <Checkbox
          css={checkAllOption}
          checked={checkAllChecked}
          indeterminate={checkAllIndeterminate}
          onChange={onCheckAllChange}
        >
          <div
            css={css`
              ${textCommonCss};
              font-weight: 500;
            `}
          >
            {allCategoriesText}
          </div>
        </Checkbox>
        <Form.Item
          css={css`
            ${formInputCss};
            margin-bottom: 0;
          `}
        >
          {getFieldDecorator(
            BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryNameInput,
            {},
          )(<Input placeholder={searchByCategoryPlaceholderText} />)}
        </Form.Item>
      </Row>
      <div css={checkboxGroupWrapCss}>
        <Form.Item>
          {getFieldDecorator(
            BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryCheckboxGroup,
          )(
            <Checkbox.Group
              css={checkboxGroupCss}
              options={inventoryCategories.map(cat => ({
                label: (
                  <div
                    key={cat.categoryID}
                    css={css`
                      ${textCommonCss};
                      font-size: 14px;
                    `}
                  >
                    {cat.name}
                  </div>
                ),
                value: cat.categoryID,
              }))}
            />,
          )}
        </Form.Item>
      </div>
    </div>
  )
}

export default InventoryCategoryCheckbox
